import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Camera, AlertTriangle } from 'lucide-react';
import type { WizardFacturaFormData } from '../../schemas/facturaSchema';

interface WizardStep1Props {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: UseFormReturn<WizardFacturaFormData, any, any>;
  esOcr: boolean;
  duplicadoWarning: any;
  onOpenUploadOCR: () => void;
  onRutChange: (rut: string) => void;
  /** Cuando es true, oculta el botón OCR (no disponible en modo edición) */
  modoEdicion?: boolean;
}

const WizardStep1DatosFactura: React.FC<WizardStep1Props> = ({
  form,
  esOcr,
  duplicadoWarning,
  onOpenUploadOCR,
  onRutChange,
  modoEdicion = false,
}) => {
  const {
    register,
    formState: { errors },
  } = form;

  return (
    <div className="space-y-4">
      {/* Botón de carga OCR: solo disponible en modo creación */}
      {!modoEdicion && (
        <button
          type="button"
          onClick={onOpenUploadOCR}
          className="w-full p-4 border-2 border-dashed border-blue-300 dark:border-blue-700 rounded-lg
                     hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors flex items-center justify-center gap-2"
        >
          <Camera className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          <span className="font-medium text-blue-600 dark:text-blue-400">
            Cargar desde imagen (OCR)
          </span>
        </button>
      )}

      {/* Alerta de OCR */}
      {esOcr && (
        <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg flex items-start gap-2">
          <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-yellow-800 dark:text-yellow-200">
            Datos extraídos por IA. Verifica que toda la información sea
            correcta antes de continuar.
          </p>
        </div>
      )}

      {/* Alerta de duplicado */}
      {duplicadoWarning && (
        <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm font-medium text-red-900 dark:text-red-100">
            Posible factura duplicada
          </p>
          <p className="text-xs text-red-600 dark:text-red-400 mt-1">
            Ya existe una factura similar en el sistema
          </p>
        </div>
      )}

      {/* Formulario */}
      <div className="grid grid-cols-2 gap-4">
        {/* Número de Factura */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Número de Factura <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            {...register('numero_factura')}
            className={`w-full px-3 py-2 border rounded-lg
                       bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100
                       ${errors.numero_factura ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
            placeholder="12345"
            aria-invalid={errors.numero_factura ? 'true' : 'false'}
            aria-describedby={errors.numero_factura ? 'error-numero-factura' : undefined}
          />
          {errors.numero_factura && (
            <p id="error-numero-factura" className="text-sm text-red-600 dark:text-red-400 mt-1">
              {errors.numero_factura.message}
            </p>
          )}
        </div>

        {/* Fecha de Emisión */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Fecha de Emisión <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            {...register('fecha_emision')}
            className={`w-full px-3 py-2 border rounded-lg
                       bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100
                       ${errors.fecha_emision ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
            aria-invalid={errors.fecha_emision ? 'true' : 'false'}
            aria-describedby={errors.fecha_emision ? 'error-fecha-emision' : undefined}
          />
          {errors.fecha_emision && (
            <p id="error-fecha-emision" className="text-sm text-red-600 dark:text-red-400 mt-1">
              {errors.fecha_emision.message}
            </p>
          )}
        </div>

        {/* RUT Proveedor */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            RUT Proveedor <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            {...register('rut_proveedor')}
            onChange={(e) => onRutChange(e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg
                       bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100
                       ${errors.rut_proveedor ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
            placeholder="12.345.678-9"
            aria-invalid={errors.rut_proveedor ? 'true' : 'false'}
            aria-describedby={errors.rut_proveedor ? 'error-rut-proveedor' : undefined}
          />
          {errors.rut_proveedor && (
            <p id="error-rut-proveedor" className="text-sm text-red-600 dark:text-red-400 mt-1">
              {errors.rut_proveedor.message}
            </p>
          )}
        </div>

        {/* Nombre Proveedor */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Nombre Proveedor <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            {...register('nombre_proveedor')}
            className={`w-full px-3 py-2 border rounded-lg
                       bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100
                       ${errors.nombre_proveedor ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
            placeholder="Nombre del proveedor"
            aria-invalid={errors.nombre_proveedor ? 'true' : 'false'}
            aria-describedby={errors.nombre_proveedor ? 'error-nombre-proveedor' : undefined}
          />
          {errors.nombre_proveedor && (
            <p id="error-nombre-proveedor" className="text-sm text-red-600 dark:text-red-400 mt-1">
              {errors.nombre_proveedor.message}
            </p>
          )}
        </div>

        {/* Método de Pago */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Método de Pago
          </label>
          <select
            {...register('metodo_pago')}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                       bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          >
            <option value="efectivo">Efectivo</option>
            <option value="transferencia">Transferencia</option>
            <option value="cheque">Cheque</option>
            <option value="credito">Crédito</option>
            <option value="debito">Débito</option>
          </select>
        </div>
      </div>

      {/* Checkbox de IVA: solo disponible en modo creación.
          En modo edición los precios almacenados en BD ya son netos (sin IVA),
          por lo que esta opción no aplica y se oculta para evitar confusión. */}
      {!modoEdicion && (
        <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-800">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              {...register('valores_incluyen_iva')}
              className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <div>
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                Los precios de esta factura incluyen IVA
              </span>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                Marca esta opción si los precios unitarios mostrados en la factura ya incluyen el IVA (19%).
                El sistema los convertirá automáticamente a precios sin IVA para almacenamiento.
              </p>
            </div>
          </label>
        </div>
      )}

      {/* Observaciones */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Observaciones
        </label>
        <textarea
          {...register('observaciones')}
          className={`w-full px-3 py-2 border rounded-lg
                     bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100
                     ${errors.observaciones ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
          rows={3}
          placeholder="Notas adicionales sobre esta factura..."
          aria-invalid={errors.observaciones ? 'true' : 'false'}
          aria-describedby={errors.observaciones ? 'error-observaciones' : undefined}
        />
        {errors.observaciones && (
          <p id="error-observaciones" className="text-sm text-red-600 dark:text-red-400 mt-1">
            {errors.observaciones.message}
          </p>
        )}
      </div>
    </div>
  );
};

export default WizardStep1DatosFactura;
